'use client'

import { StoryCard } from '@/components/story-card'
import { useStoryRecommendations } from '@/hooks/useStoryRecommendations'
import type { StoryLearningIndexItem } from '@/content/stories'

export function StoryRecommendationsSection({
  stories,
  currentSlug = null,
  title = 'Next reads',
  description = 'Picked from your progress, familiar words, and story history.',
  limit = 5
}: {
  stories: StoryLearningIndexItem[]
  currentSlug?: string | null
  title?: string
  description?: string
  limit?: number
}) {
  const { data: recommendations = [], isLoading } = useStoryRecommendations({
    stories,
    currentSlug,
    limit
  })

  if (!recommendations.length && !isLoading) return null

  return (
    <section className="min-w-0 space-y-3">
      <div className="flex min-w-0 items-end justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">{title}</h2>
          {description && (
            <p className="text-muted-foreground mt-1 text-sm">{description}</p>
          )}
        </div>
      </div>
      <div className="-mx-4 overflow-x-auto px-4 pb-2 [scrollbar-width:thin] sm:-mx-1 sm:px-1">
        <div className="flex snap-x snap-mandatory items-stretch gap-4">
          {recommendations.map((recommendation) => (
            <StoryCard
              key={recommendation.story.slug}
              story={recommendation.story}
              readStoryText="Read story"
              href={`/read/${recommendation.story.slug}`}
              learningState={recommendation.progress}
              recommendationReasons={recommendation.reasons}
              familiarityPercent={
                recommendation.familiarity.familiarityScore * 100
              }
              variant="compact"
              className="w-[min(76vw,16.5rem)] shrink-0 snap-start sm:w-64 lg:w-64"
            />
          ))}
          {!recommendations.length &&
            isLoading &&
            Array.from({ length: Math.min(3, limit) }).map((_, index) => (
              <div
                key={index}
                className="bg-card min-h-48 w-[min(82vw,22rem)] shrink-0 snap-start rounded-lg border p-5 sm:w-80 lg:w-[21rem]"
              >
                <div className="bg-muted h-3 w-20 rounded" />
                <div className="bg-muted mt-4 h-6 w-32 rounded" />
                <div className="bg-muted mt-3 h-4 w-44 rounded" />
                <div className="mt-5 flex gap-2">
                  <div className="bg-muted h-5 w-20 rounded-full" />
                  <div className="bg-muted h-5 w-24 rounded-full" />
                </div>
              </div>
            ))}
        </div>
      </div>
    </section>
  )
}
